import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { canAccessStorePage } from 'app/shared/entitlement/store-access.util';
import { apiSilentRequestOptions } from 'app/shared/interceptors/api-message-context';

export type StoreVisualTestMatrixCase = {
    id: string;
    label: string;
    group: string;
    storefront_path: string;
};

export type TenantStoreCategory = {
    code: string;
    name: string;
    description: string | null;
    status: string;
    sort_order: number;
    banner?: TenantStoreBanner | null;
    popup?: TenantStoreBanner | null;
};

export type TenantStoreCategoryInput = {
    name?: string;
    description?: string | null;
    status?: string;
    sort_order?: number;
    banner?: TenantStoreBanner | null;
    popup?: TenantStoreBanner | null;
};

export type TenantStorePromoCode = {
    id: string;
    code: string;
    discount_type: 'percent' | 'fixed' | string;
    discount_value: number;
    min_order_total?: number | null;
    max_uses: number | null;
    max_uses_per_user?: number | null;
    allowed_emails?: string[] | null;
    uses_count: number;
    uses_percent?: number | null;
    expires_at: string | null;
    active: boolean;
    created_at?: string | null;
    updated_at?: string | null;
};

export type TenantStoreNewsletter = {
    id: string;
    title: string;
    status: string;
    subject?: string | null;
    body?: string | null;
    audience_size?: string | null;
    updated_at?: string | null;
};

export type TenantStorePromoCodeInput = {
    code?: string;
    discount_type?: string;
    discount_value?: number;
    min_order_total?: number | null;
    max_uses?: number | null;
    max_uses_per_user?: number | null;
    allowed_emails?: string[] | null;
    expires_at?: string | null;
    active?: boolean;
};

export type TenantStoreBranding = {
    name?: string;
    description?: string;
    copyright?: string;
    address?: string;
    phone?: string;
    email?: string;
    adminEmail?: string;
    adminEmails?: string[];
    map?: string;
    logo?: string;
    favicon?: string;
    watermark?: string;
    colorPrimary?: string;
    colorSecondary?: string;
    colorAccent?: string;
    colorHeader?: string;
    colorFooter?: string;
    colorBackground?: string;
    colorText?: string;
    colorButton?: string;
    colorLink?: string;
    email_template?: TenantStoreEmailTemplate;
    [key: string]: unknown;
};

export type TenantStoreEmailTemplate = {
    template_key?: string;
    header_html?: string;
    signature_html?: string;
    from_email?: string;
    from_name?: string;
    subject_prefix?: string;
    disclaimer?: string;
    header_title?: string;
    email_subtitle?: string;
    body_greeting?: string;
    body_intro?: string;
    body_closing?: string;
    footer_text?: string;
    otp_section_label?: string;
    layout?: string;
    colors?: Record<string, string>;
};

export type TenantStoreDocumentKind = 'quote' | 'invoice' | 'receipt';

export type TenantStoreDocumentRule = {
    layout_id?: string | null;
    template_key?: string;
    custom_message_html?: string | null;
    trigger_status?: string;
    send_email?: boolean;
    email_body?: string | null;
    layout?: { id: string; name: string } | null;
    layout_edit_path?: string | null;
};

export type TenantStoreDocumentSettings = {
    documents?: Record<TenantStoreDocumentKind, TenantStoreDocumentRule>;
    available_layouts?: Array<{ id: string; name: string }>;
    document_templates?: Array<{ key: string; label: string }>;
    imported_layouts?: Record<string, string>;
};

export type TenantStorePaymentMethodControl =
    | 'methodVisa'
    | 'methodMastercard'
    | 'methodAmex'
    | 'methodPaypal'
    | 'methodDiscover'
    | 'methodMytMoney'
    | 'methodMcbJuice'
    | 'methodBankTransfer';

export type TenantStorePayment = {
    bankName?: string;
    accountName?: string;
    accountNumber?: string;
    swiftBic?: string;
    paymentNote?: string;
    bankInstructions?: string;
    allowProofUpload?: boolean;
    proofMaxMb?: number;
    /** Auto-cancel unpaid invoices after N hours (pending_payment only). 0 = off. */
    unpaidAutoCancelHours?: number;
    /** Keep cancelled invoices visible on My account for N days. */
    cancelledVisibleDays?: number;
    methodVisa?: boolean;
    methodMastercard?: boolean;
    methodAmex?: boolean;
    methodPaypal?: boolean;
    methodDiscover?: boolean;
    methodMytMoney?: boolean;
    methodMcbJuice?: boolean;
    methodBankTransfer?: boolean;
    /** Per-method checkout instructions (HTML). */
    methodInstructions?: Partial<Record<TenantStorePaymentMethodControl, string>>;
    [key: string]: unknown;
};

export type TenantStoreCheckout = {
    otpAtCheckout?: boolean;
    rememberDeviceDays?: number;
    extendRememberDeviceOnVisit?: boolean;
    notifyAdminOnPaymentReceived?: boolean;
    [key: string]: unknown;
};

export type TenantStoreBannerTextLayer = {
    id: string;
    /** Rich-text HTML for this positioned text box. */
    html: string;
    /** Horizontal anchor (% of canvas width). */
    x: number;
    /** Vertical anchor (% of canvas height). */
    y: number;
    /** Box width (% of canvas width). */
    width: number;
    /** Box height (% of canvas height). Omit for auto height from content. */
    height?: number;
};

export type TenantStoreBanner = {
    image?: string;
    href?: string;
    /** Admin label for this banner zone (home hero, promo tile, etc.). */
    name?: string;
    /** Call-to-action button label (entry popups). */
    cta_label?: string;
    title?: string;
    /** Rich-text overlay (HTML from the BO WYSIWYG) shown on top of the banner. */
    content?: string;
    /** GIMP-style positioned text boxes (preferred over legacy centred `content`). */
    layers?: TenantStoreBannerTextLayer[];
    enabled?: boolean;
};

export type TenantStoreBanners = {
    hero?: TenantStoreBanner;
    grid?: TenantStoreBanner[];
    feature?: TenantStoreBanner;
};

export type TenantStoreLanguage = {
    code: string;
    label: string;
    enabled?: boolean;
    /** Right-to-left layout when this language is active on the storefront. */
    rtl?: boolean;
};

export type TenantStoreCurrency = {
    code: string;
    label: string;
    symbol: string;
    rate: number;
    enabled?: boolean;
};

export type TenantStoreLocalization = {
    languages?: TenantStoreLanguage[];
    default_language?: string;
    translations?: Record<string, Record<string, string>>;
    currencies?: TenantStoreCurrency[];
    base_currency?: string;
    pricing_mode?: 'exchange_rate' | 'manual';
};

export type TenantStoreSettings = {
    retail?: {
        layouts?: {
            home?: string;
            category?: string;
            product?: string;
            theme_color?: string;
            rtl?: boolean;
            dark?: boolean;
        };
        chrome?: {
            cookie_bar?: { enabled?: boolean; position?: string; content?: string };
            social?: Record<string, { enabled?: boolean; url?: string }>;
            newsletter?: { enabled?: boolean };
            brands?: Array<{ label?: string; logo?: string; url?: string; enabled?: boolean }>;
            [key: string]: unknown;
        };
        localization?: TenantStoreLocalization;
        banners?: TenantStoreBanners;
        inventory_mode?: string;
        /** Global catalogue low-stock alert level when a product has no override. */
        default_low_stock_threshold?: number | null;
        [key: string]: unknown;
    };
    general?: TenantStoreBranding;
    timeline?: {
        payment?: TenantStorePayment;
        checkout?: TenantStoreCheckout;
        [key: string]: unknown;
    };
    assets_base?: string;
    [key: string]: unknown;
};

export type TenantStoreProduct = {
    id: string;
    kind: 'retail' | 'bundle';
    name: string;
    slug?: string;
    sku?: string | null;
    price: number;
    currency: string;
    price_includes_vat: boolean;
    vat_rate: number;
    stock_qty: number;
    low_stock_threshold?: number | null;
    status: string;
    description?: string | null;
    image?: string | null;
    images?: string[];
    video_url?: string | null;
    ribbon?: TenantStoreProductRibbon | null;
    offers_top?: TenantStoreProductOffer[];
    offers_bottom?: TenantStoreProductOffer[];
    /** @deprecated Use offers_bottom */
    offers?: TenantStoreProductOffer[];
    seo_title?: string | null;
    seo_description?: string | null;
    related_product_ids?: string[];
    category_code?: string | null;
    /** Full category membership — a product can belong to more than one category. */
    categories?: string[];
    prices?: Record<string, number>;
    bundle_items?: Array<{ product_id: string; product_name?: string | null; qty: number }>;
    updated_at?: string | null;
};

export type TenantStoreProductRibbon = {
    enabled?: boolean;
    text?: string;
    style?: 'corner' | 'round' | string;
};

export type TenantStoreProductOffer = {
    icon?: string;
    text?: string;
};

export type TenantStoreOrder = {
    order_id: string;
    token?: string;
    reference?: string;
    status: string;
    order_status?: string;
    status_label?: string;
    customer_id?: string | null;
    customer_name: string;
    customer_type?: string | null;
    quote_pending?: boolean;
    quote_note?: string | null;
    quote_reply?: string | null;
    email: string;
    phone?: string;
    total: number;
    currency: string;
    payment_status?: string;
    payment_proof?: {
        filename?: string;
        content_type?: string;
        uploaded_at?: string;
    } | null;
    payment_proof_upload_token?: string;
    promo_code?: string | null;
    discount?: number | null;
    address?: string | null;
    delivery?: {
        district_code: string;
        district_name: string;
        weekday: string;
        price: number;
    } | null;
    lines: Array<{
        id?: string;
        product_id: string;
        product_name?: string;
        kind?: string;
        qty: number;
        unit_price: number | null;
        line_total: number | null;
        catalog_unit_price?: number | null;
        bundle_components?: Array<{ product_id: string; name: string; qty: number }>;
    }>;
    created_at?: string;
};

export type TenantStoreUser = {
    user_id: number;
    email: string;
    name: string;
    othernames?: string;
    lastname?: string;
    roles: string[];
    role_labels: string[];
    status: string;
};

export type TenantStoreSubscriber = {
    id: string;
    email: string;
    name: string | null;
    status: string;
    source: string | null;
    consent_at: string | null;
    created_at: string | null;
};

export type TenantStoreReview = {
    id: number;
    product_id: string;
    product_name?: string | null;
    reviewer_name: string;
    email?: string;
    rating: number;
    title: string | null;
    body: string | null;
    merchant_reply?: string | null;
    photos?: string[];
    status: 'pending' | 'approved' | 'rejected' | string;
    verified_buyer?: boolean;
    created_at?: string | null;
};

import { TenantStorePageContact } from './contact-page.util';

export type TenantStorePageBlock = {
    type: string;
    html?: string;
    text?: string;
    level?: number;
    variant?: string;
    size?: string;
    columns?: TenantStorePageBlock[][];
    contact?: import('./contact-page.util').TenantStorePageContact;
};

export type TenantStorePageElement = {
    id: string;
    type: string;
    label?: string;
    enabled?: boolean;
};

export type TenantStorePage = {
    id: string;
    slug: string;
    title: string;
    status: 'draft' | 'published' | string;
    show_in_footer: boolean;
    footer_label: string | null;
    footer_sort: number;
    updated_at?: string | null;
    blocks?: TenantStorePageBlock[];
    banner?: TenantStoreBanner | null;
    /** Per-page entry popup (same shape as welcome popup / banner). */
    popup?: TenantStoreBanner | null;
    seo_title?: string | null;
    seo_description?: string | null;
    layout?: string | null;
    contact?: TenantStorePageContact | null;
    kind?: 'system' | 'cms' | string;
    system_role?: 'home' | 'category' | 'product' | string | null;
    layout_key?: string | null;
    elements?: TenantStorePageElement[];
    locked?: boolean;
};

export type TenantStorePageInput = {
    title?: string;
    slug?: string;
    status?: string;
    blocks?: TenantStorePageBlock[];
    banner?: TenantStoreBanner | null;
    popup?: TenantStoreBanner | null;
    show_in_footer?: boolean;
    footer_label?: string | null;
    footer_sort?: number;
    seo_title?: string | null;
    seo_description?: string | null;
    layout?: string | null;
    contact?: TenantStorePageContact | null;
    elements?: TenantStorePageElement[];
};

export type StoreOverviewExtras = {
    abandoned_carts?: { total?: number; reminded?: number; pending?: number };
    stock_alerts?: { signups?: number; products?: number };
    reviews?: {
        available?: boolean;
        total?: number;
        pending?: number;
        approved?: number;
        avg_rating?: number | null;
        products_without_reviews?: number;
    };
    subscribers?: {
        available?: boolean;
        total?: number;
        subscribed?: number;
        unsubscribed?: number;
        pending?: number;
    };
    promo_codes?: Array<{
        id: string;
        code: string;
        uses_count: number;
        active: boolean;
    }>;
};

@Injectable({ providedIn: 'root' })
export class TenantStoreApiService {
    constructor(private _http: HttpClient) {}

    private base(): string | null {
        const org = environment.selection?.organization?.code || environment.selection?.organization?.slug;
        const appId = environment.app?.id ?? environment.user?.app_id;
        if (!org || !appId) {
            return null;
        }
        // Relative /api path so ApiRequestInterceptor attaches auth + tenant headers
        // and rewrites to the API host. Absolute URLs bypass that interceptor (no token → 401).
        return `/api/${appId}/${org}/store`;
    }

    /**
     * @param silent When true, suppress the global loading overlay — use on list
     *   pages that already render an inline table spinner (avoids two spinners).
     */
    listProducts(admin = true, silent = false): Observable<TenantStoreProduct[]> {
        const base = this.base();
        if (!base) {
            return of([]);
        }
        const scope = admin ? '?scope=admin' : '';
        const options = silent ? apiSilentRequestOptions() : {};
        return this._http.get<{ data?: TenantStoreProduct[] }>(`${base}/products${scope}`, options).pipe(
            map((res) => res?.data ?? []),
            // Admin scope is manager-gated; fall back to the public (active) list for viewers.
            catchError(() => (admin ? this.listProducts(false, silent) : of([])))
        );
    }

    getProduct(productId: string): Observable<TenantStoreProduct | null> {
        return this.listProducts(true).pipe(
            map((rows) => rows.find((row) => row.id === productId) ?? null),
        );
    }

    createProduct(body: Record<string, unknown>): Observable<TenantStoreProduct | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.post<TenantStoreProduct>(`${base}/products`, body).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    updateProduct(productId: string, body: Record<string, unknown>): Observable<TenantStoreProduct | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.patch<TenantStoreProduct>(`${base}/products/${productId}`, body).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    uploadProductImage(productId: string, file: File): Observable<TenantStoreProduct | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        const form = new FormData();
        form.append('file', file);
        return this._http.post<TenantStoreProduct>(`${base}/products/${productId}/image`, form).pipe(
            map((res) => res ?? null),
            catchError(() => of(null)),
        );
    }

    importProducts(rows: Record<string, unknown>[]): Observable<{ created: number; updated: number } | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.post<{ created: number; updated: number }>(`${base}/products/import`, { rows }).pipe(
            map((res) => res ?? null),
            catchError(() => of(null)),
        );
    }

    createBundle(body: Record<string, unknown>): Observable<TenantStoreProduct | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.post<TenantStoreProduct>(`${base}/bundles`, body).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    updateBundle(bundleId: string, body: Record<string, unknown>): Observable<TenantStoreProduct | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.patch<TenantStoreProduct>(`${base}/bundles/${bundleId}`, body).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    /**
     * @param silent When true, suppress the global loading overlay — use on list
     *   pages that already render an inline table spinner (avoids two spinners).
     */
    listOrders(silent = false): Observable<TenantStoreOrder[]> {
        const base = this.base();
        if (!base) {
            return of([]);
        }
        const options = silent ? apiSilentRequestOptions() : {};
        return this._http.get<{ data?: TenantStoreOrder[] }>(`${base}/orders`, options).pipe(
            map((res) => res?.data ?? []),
            catchError(() => of([]))
        );
    }

    getOverviewStats(silent = true): Observable<StoreOverviewExtras | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        const options = silent ? apiSilentRequestOptions() : {};
        return this._http.get<StoreOverviewExtras>(`${base}/overview/stats`, options).pipe(
            map((res) => res ?? null),
            catchError(() => of(null)),
        );
    }

    /**
     * Newsletter subscribers captured by the storefront opt-in form.
     * @param silent When true, suppress the global loading overlay (inline spinner instead).
     */
    listSubscribers(silent = false): Observable<TenantStoreSubscriber[]> {
        const base = this.base();
        if (!base) {
            return of([]);
        }
        const options = silent ? apiSilentRequestOptions() : {};
        return this._http.get<{ data?: TenantStoreSubscriber[] }>(`${base}/subscribers`, options).pipe(
            map((res) => res?.data ?? []),
            catchError(() => of([]))
        );
    }

    unsubscribeContact(contactId: string): Observable<TenantStoreSubscriber | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.patch<TenantStoreSubscriber>(`${base}/subscribers/${contactId}/unsubscribe`, {}).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    /** CMS pages (admin scope = all statuses incl. drafts). */
    listPages(silent = false): Observable<TenantStorePage[]> {
        const base = this.base();
        if (!base) {
            return of([]);
        }
        const options = silent ? apiSilentRequestOptions() : {};
        return this._http.get<{ data?: TenantStorePage[] }>(`${base}/pages?scope=admin`, options).pipe(
            map((res) => res?.data ?? []),
            catchError(() => of([]))
        );
    }

    getPage(pageId: string): Observable<TenantStorePage | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.get<TenantStorePage>(`${base}/pages/${pageId}`).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    createPage(body: TenantStorePageInput): Observable<TenantStorePage | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.post<TenantStorePage>(`${base}/pages`, body).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    updatePage(pageId: string, body: TenantStorePageInput): Observable<TenantStorePage | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.patch<TenantStorePage>(`${base}/pages/${pageId}`, body).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    deletePage(pageId: string): Observable<boolean> {
        const base = this.base();
        if (!base) {
            return of(false);
        }
        return this._http.delete(`${base}/pages/${pageId}`).pipe(
            map(() => true),
            catchError(() => of(false))
        );
    }

    issueBulkQuote(
        orderId: string,
        body: { message: string; lines: Array<{ id: string; unit_price: number }> },
    ): Observable<{ order: TenantStoreOrder | null; error: string }> {
        const base = this.base();
        if (!base) {
            return of({ order: null, error: 'Store is not available.' });
        }
        return this._http.post<TenantStoreOrder>(`${base}/orders/${orderId}/quote`, body).pipe(
            map((res) => ({ order: res ?? null, error: '' })),
            catchError((err: { error?: { message?: string } }) => of({
                order: null,
                error: err?.error?.message || 'Could not send the quote.',
            })),
        );
    }

    confirmPayment(orderId: string, comment?: string): Observable<TenantStoreOrder | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        const body = comment?.trim() ? { comment: comment.trim() } : {};
        return this._http.post<TenantStoreOrder>(`${base}/orders/${orderId}/confirm-payment`, body).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    rejectPayment(orderId: string, comment: string): Observable<TenantStoreOrder | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.post<TenantStoreOrder>(`${base}/orders/${orderId}/reject-payment`, {
            comment: comment.trim(),
        }).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    fetchPaymentProof(orderId: string): Observable<Blob> {
        const base = this.base();
        if (!base) {
            return of(new Blob());
        }
        return this._http.get(`${base}/orders/${orderId}/payment-proof`, { responseType: 'blob' });
    }

    /**
     * @param silent When true, suppress the global loading overlay — use on list
     *   pages that already render an inline table spinner (avoids two spinners).
     */
    listStoreUsers(silent = false): Observable<TenantStoreUser[]> {
        const base = this.base();
        if (!base) {
            return of([]);
        }
        const options = silent ? apiSilentRequestOptions() : {};
        return this._http.get<{ data?: TenantStoreUser[] }>(`${base}/users`, options).pipe(
            map((res) => res?.data ?? []),
            catchError(() => of([]))
        );
    }

    createStoreUser(body: { email: string; othernames?: string; lastname?: string; role: 'manager' | 'accounts'; password?: string }): Observable<unknown> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.post(`${base}/users`, body);
    }

    updateStoreUser(
        userId: number,
        body: { othernames?: string; lastname?: string; email?: string; roles: string[] },
    ): Observable<unknown> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.patch(`${base}/users/${userId}`, body);
    }

    /** Activate / deactivate a store team member's account. */
    setStoreUserStatus(userId: number, status: 'ACTIVE' | 'INACTIVE'): Observable<unknown> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.patch(`${base}/users/${userId}/status`, { status });
    }

    getSettings(silent = true): Observable<TenantStoreSettings | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        const options = silent ? apiSilentRequestOptions() : {};
        return this._http.get<TenantStoreSettings>(`${base}/settings`, options).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    updateSettings(patch: Record<string, unknown>): Observable<unknown> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.patch(`${base}/settings`, patch);
    }

    getDocumentSettings(): Observable<TenantStoreDocumentSettings | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.get<TenantStoreDocumentSettings>(`${base}/settings/documents`, apiSilentRequestOptions()).pipe(
            map((res) => res ?? null),
            catchError(() => of(null)),
        );
    }

    saveDocumentSettings(
        documents: Partial<Record<TenantStoreDocumentKind, Partial<TenantStoreDocumentRule>>>,
    ): Observable<TenantStoreDocumentSettings | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.patch<TenantStoreDocumentSettings>(`${base}/settings/documents`, { documents }).pipe(
            map((res) => res ?? null),
            catchError(() => of(null)),
        );
    }

    importMultikartLayouts(): Observable<TenantStoreDocumentSettings | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http
            .post<TenantStoreDocumentSettings>(`${base}/settings/documents/import-multikart-layouts`, {})
            .pipe(
                map((res) => res ?? null),
                catchError(() => of(null)),
            );
    }

    getDocumentPreview(
        kind: TenantStoreDocumentKind,
        templateKey: string,
        customMessageHtml = '',
        layoutId = '',
    ): Observable<{ html: string; preview_mode?: string; preview_url?: string }> {
        const base = this.base();
        if (!base) {
            return of({ html: '' });
        }
        const params = new URLSearchParams({
            kind,
            template_key: templateKey,
            custom_message_html: customMessageHtml,
        });
        if (`${layoutId ?? ''}`.trim()) {
            params.set('layout_id', `${layoutId}`.trim());
        }
        return this._http
            .get<{ html?: string; preview_mode?: string; preview_url?: string }>(
                `${base}/settings/documents/preview?${params.toString()}`,
                apiSilentRequestOptions(),
            )
            .pipe(
                map((res) => ({
                    html: `${res?.html ?? ''}`,
                    preview_mode: res?.preview_mode,
                    preview_url: res?.preview_url,
                })),
                catchError(() => of({ html: '' })),
            );
    }

    documentLayoutPreviewUrl(layoutId: string): string {
        const base = this.base();
        if (!base || !`${layoutId ?? ''}`.trim()) {
            return '';
        }
        const params = new URLSearchParams({ layout_id: `${layoutId}`.trim() });
        return `${base}/settings/documents/layout-preview?${params.toString()}`;
    }

    getVisualTestMatrix(): Observable<{ cases?: StoreVisualTestMatrixCase[]; storefront_base?: string } | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.get<{ cases?: StoreVisualTestMatrixCase[]; storefront_base?: string }>(
            `${base}/settings/visual-test/matrix`,
        ).pipe(
            map((res) => res ?? null),
            catchError(() => of(null)),
        );
    }

    runVisualTests(storefrontBase = ''): Observable<unknown> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.post(`${base}/settings/visual-test/run`, {
            storefront_base: storefrontBase || undefined,
        });
    }

    getVisualTestReport(): Observable<Record<string, unknown> | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.get<Record<string, unknown>>(`${base}/settings/visual-test/report`).pipe(
            map((res) => res ?? null),
            catchError(() => of(null)),
        );
    }

    visualTestAssetOrigin(): string {
        const base = this.base();
        if (!base) {
            return `${window.location.origin}`;
        }
        const match = base.match(/^(https?:\/\/[^/]+)/);
        return match ? match[1] : `${window.location.origin}`;
    }

    visualTestScreenshotUrl(fileName: string): string {
        const base = this.base();
        if (!base || !fileName) {
            return '';
        }
        return `${base}/settings/visual-test/screenshots/${encodeURIComponent(fileName)}`;
    }

    /** Fetch a screenshot with auth and return a blob object URL for <img> binding. */
    fetchVisualTestScreenshot(fileName: string): Observable<string> {
        const url = this.visualTestScreenshotUrl(fileName);
        if (!url) {
            return of('');
        }
        return this._http.get(url, { responseType: 'blob' }).pipe(
            map((blob) => URL.createObjectURL(blob)),
            catchError(() => of('')),
        );
    }

    /**
     * Upload a branding asset (logo / favicon / wallpaper) to the tenant NAS and
     * resolve to its public `/assets/layout/<kind>.<ext>` path. Stored verbatim in
     * the branding form so the storefront serves a real file instead of a data URI.
     */
    uploadBranding(kind: 'logo' | 'favicon' | 'watermark', file: File): Observable<string> {
        const base = this.base();
        if (!base) {
            return of('');
        }
        const form = new FormData();
        form.append('asset_kind', kind);
        form.append('file', file);
        return this._http
            .post<{ url?: string; download?: string; path?: string }>(`${base}/settings/branding`, form)
            .pipe(map((res) => `${res?.url ?? res?.download ?? res?.path ?? ''}`));
    }

    /**
     * Upload a home banner image to the tenant NAS and resolve to its public
     * `/assets/layout/banners/<file>` path, stored verbatim in the banners block.
     */
    uploadBanner(file: File): Observable<string> {
        const base = this.base();
        if (!base) {
            return of('');
        }
        const form = new FormData();
        form.append('file', file);
        return this._http
            .post<{ url?: string; download?: string; path?: string }>(`${base}/settings/banner-image`, form)
            .pipe(map((res) => `${res?.url ?? res?.download ?? res?.path ?? ''}`));
    }

    /** Replace the storefront home banners (hero + promo grid). */
    saveBanners(banners: TenantStoreBanners): Observable<TenantStoreSettings | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.put<TenantStoreSettings>(`${base}/settings/banners`, { banners }).pipe(
            map((res) => res ?? null),
            catchError(() => of(null)),
        );
    }

    canManageBanners(): boolean {
        return canAccessStorePage('banners');
    }

    /**
     * @param silent When true, suppress the global loading overlay — use on list
     *   pages that already render an inline table spinner (avoids two spinners).
     */
    listCategories(silent = false): Observable<TenantStoreCategory[]> {
        const base = this.base();
        if (!base) {
            return of([]);
        }
        const options = silent ? apiSilentRequestOptions() : {};
        return this._http.get<{ data?: TenantStoreCategory[] }>(`${base}/categories`, options).pipe(
            map((res) => res?.data ?? []),
            catchError(() => of([]))
        );
    }

    getCategory(code: string): Observable<TenantStoreCategory | null> {
        return this.listCategories().pipe(
            map((rows) => rows.find((row) => row.code === code) ?? null),
        );
    }

    createCategory(body: TenantStoreCategoryInput): Observable<TenantStoreCategory | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.post<TenantStoreCategory>(`${base}/categories`, body).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    updateCategory(code: string, body: Partial<TenantStoreCategoryInput>): Observable<TenantStoreCategory | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.patch<TenantStoreCategory>(`${base}/categories/${code}`, body).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    deleteCategory(code: string): Observable<boolean> {
        const base = this.base();
        if (!base) {
            return of(false);
        }
        return this._http.delete(`${base}/categories/${code}`).pipe(
            map(() => true),
            catchError(() => of(false))
        );
    }

    listPromos(silent = false): Observable<TenantStorePromoCode[]> {
        const base = this.base();
        if (!base) {
            return of([]);
        }
        const options = silent ? apiSilentRequestOptions() : {};
        return this._http.get<{ data?: TenantStorePromoCode[] }>(`${base}/promos`, options).pipe(
            map((res) => res?.data ?? []),
            catchError(() => of([]))
        );
    }

    getPromo(id: string): Observable<TenantStorePromoCode | null> {
        return this.listPromos().pipe(
            map((rows) => rows.find((row) => row.id === id) ?? null),
        );
    }

    createPromo(body: TenantStorePromoCodeInput): Observable<TenantStorePromoCode | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.post<TenantStorePromoCode>(`${base}/promos`, body).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    updatePromo(id: string, body: Partial<TenantStorePromoCodeInput>): Observable<TenantStorePromoCode | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.patch<TenantStorePromoCode>(`${base}/promos/${id}`, body).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    deletePromo(id: string): Observable<boolean> {
        const base = this.base();
        if (!base) {
            return of(false);
        }
        return this._http.delete(`${base}/promos/${id}`).pipe(
            map(() => true),
            catchError(() => of(false))
        );
    }

    canManagePromos(): boolean {
        return canAccessStorePage('promos');
    }

    listNewsletters(silent = false): Observable<TenantStoreNewsletter[]> {
        const base = this.base();
        if (!base) {
            return of([]);
        }
        const options = silent ? apiSilentRequestOptions() : {};
        return this._http.get<{ data?: TenantStoreNewsletter[] }>(`${base}/newsletters`, options).pipe(
            map((res) => res?.data ?? []),
            catchError(() => of([])),
        );
    }

    getNewsletter(id: string): Observable<TenantStoreNewsletter | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.get<TenantStoreNewsletter>(`${base}/newsletters/${id}`).pipe(
            map((res) => res ?? null),
            catchError(() => of(null)),
        );
    }

    createNewsletter(body: {
        title: string;
        status?: string;
        subject?: string | null;
        body?: string | null;
    }): Observable<TenantStoreNewsletter | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.post<TenantStoreNewsletter>(`${base}/newsletters`, body).pipe(
            map((res) => res ?? null),
            catchError(() => of(null)),
        );
    }

    updateNewsletter(
        id: string,
        body: Partial<{ title: string; status: string; subject: string | null; body: string | null }>,
    ): Observable<TenantStoreNewsletter | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.patch<TenantStoreNewsletter>(`${base}/newsletters/${id}`, body).pipe(
            map((res) => res ?? null),
            catchError(() => of(null)),
        );
    }

    deleteNewsletter(id: string): Observable<boolean> {
        const base = this.base();
        if (!base) {
            return of(false);
        }
        return this._http.delete(`${base}/newsletters/${id}`).pipe(
            map(() => true),
            catchError(() => of(false)),
        );
    }

    canManageNewsletters(): boolean {
        return canAccessStorePage('newsletters');
    }

    listBranches(): Observable<Array<{ id: number; code: string; name: string }>> {
        const base = this.base();
        if (!base) {
            return of([]);
        }
        return this._http.get<{ data?: Array<{ id: number; code: string; name: string }> }>(`${base}/branches`).pipe(
            map((res) => res?.data ?? []),
            catchError(() => of([]))
        );
    }

    updateStock(productId: string, qty: number, branchId?: number): Observable<unknown> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.patch(`${base}/products/${productId}/stock`, {
            qty,
            branch_id: branchId ?? null,
        });
    }

    fulfillOrder(orderId: string, comment?: string): Observable<TenantStoreOrder | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        const body = comment?.trim() ? { comment: comment.trim() } : {};
        return this._http.post<TenantStoreOrder>(`${base}/orders/${orderId}/fulfill`, body).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    isManager(): boolean {
        const roles = environment.user?.roles as Record<string, boolean> | undefined;
        return !!(roles?.manager || roles?.admin || environment.user?.superadmin);
    }

    isAccounts(): boolean {
        const roles = environment.user?.roles as Record<string, boolean> | undefined;
        return !!(roles?.accounts || roles?.accountant);
    }

    canManageCatalog(): boolean {
        return canAccessStorePage('catalog');
    }

    canManageCategories(): boolean {
        return canAccessStorePage('categories');
    }

    canConfirmPayment(): boolean {
        return canAccessStorePage('orders');
    }

    canManageReviews(): boolean {
        return canAccessStorePage('reviews');
    }

    /**
     * Product reviews awaiting moderation (and already moderated ones).
     * @param silent When true, suppress the global loading overlay (inline spinner instead).
     */
    listReviews(silent = false): Observable<TenantStoreReview[]> {
        const base = this.base();
        if (!base) {
            return of([]);
        }
        const options = silent ? apiSilentRequestOptions() : {};
        return this._http.get<{ data?: TenantStoreReview[] }>(`${base}/reviews`, options).pipe(
            map((res) => res?.data ?? []),
            catchError(() => of([]))
        );
    }

    approveReview(reviewId: number): Observable<TenantStoreReview | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.post<TenantStoreReview>(`${base}/reviews/${reviewId}/approve`, {}).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    rejectReview(reviewId: number): Observable<TenantStoreReview | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.post<TenantStoreReview>(`${base}/reviews/${reviewId}/reject`, {}).pipe(
            map((res) => res ?? null),
            catchError(() => of(null))
        );
    }

    deleteReview(reviewId: number): Observable<boolean> {
        const base = this.base();
        if (!base) {
            return of(false);
        }
        return this._http.delete(`${base}/reviews/${reviewId}`).pipe(
            map(() => true),
            catchError(() => of(false))
        );
    }

    replyToReview(reviewId: number, merchantReply: string): Observable<TenantStoreReview | null> {
        const base = this.base();
        if (!base) {
            return of(null);
        }
        return this._http.patch<TenantStoreReview>(`${base}/reviews/${reviewId}/reply`, {
            merchant_reply: merchantReply,
        }).pipe(
            map((res) => res ?? null),
            catchError(() => of(null)),
        );
    }
}
